import React, { createContext, useContext, useState, useRef, useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { useMusicPlayer } from './MusicPlayerContext.jsx'
import { useOverlayManager } from './overlayManager.jsx'

// Camera positions and rotations - fixed viewpoints
const CAMERA_STATES = {
  outside: {
    position: [0, 1.8, 16],
    rotation: [0, 0], // [xDegrees, yDegrees]
    fov: 30
  },
  doorbell: {
    position: [0, 1.6, 6],
    rotation: [0, 0],
    fov: 60
  },
  lounge: {
    position: [-1, 1.8, 4],
    rotation: [0, -45], // Negative 45 degrees
    fov: 90
  },
  office: {
    position: [-1.5, 3.2, -1.5],
    rotation: [-20, -70], // Negative 70 degrees
    fov: 75,
  },
  gallery: {
    position: [0, 1.8, 3],
    rotation: [0, -90], // Negative 90 degrees
    fov: 60,
  },
  music: {
    position: [0, 1.8, 0],
    rotation: [-20, 0], // Negative 20 degrees
    fov: 60,
  },
  about: {
    position: [0, 2.8, -3.8],
    rotation: [0, -90], // Looking at the monitor screen
    fov: 45,
  },
  contact: {
    position: [-0.5, 3, -2],
    rotation: [-60, -155], // Looking down at the notebook
    fov: 60,
  }
}

// Parent-child relationships for ESC navigation
const CAMERA_PARENTS = {
  gallery: 'lounge',
  music: 'lounge', 
  office: 'lounge',
  about: 'office',
  contact: 'office'
}

// Simple global context
const CameraContext = createContext()

// Provider component (no R3F hooks here)
export function CameraProvider({ children }) {
  const [visibleInteractions, setVisibleInteractions] = useState(['doorbell'])
  const [showConversation, setShowConversation] = useState(false)
  const [showGallery, setShowGallery] = useState(false)
  const [showMusic, setShowMusic] = useState(false)
  const [showContact, setShowContact] = useState(false)
  const [showAbout, setShowAbout] = useState(false)
  const [showCredits, setShowCredits] = useState(false)
  const [hasEnteredHouse, setHasEnteredHouse] = useState(false)
  
  // Scene references for animations
  const sceneRefs = useRef({
    ambientLight: null,
    rainComponent: null
  })

  // Camera controls ref
  const cameraControlsRef = useRef({})

  const [currentCameraState, setCurrentCameraState] = useState('outside')

  const value = {
    visibleInteractions,
    sceneRefs,
    cameraControlsRef,
    setVisibleInteractions,
    showConversation,
    setShowConversation,
    showGallery,
    setShowGallery,
    showMusic,
    setShowMusic,
    showContact,
    setShowContact,
    showAbout,
    setShowAbout,
    showCredits,
    setShowCredits,
    hasEnteredHouse,
    currentCameraState,
    setCurrentCameraState,
    isVisible: (objectKey) => visibleInteractions.includes(objectKey),
    // Camera animation functions
    moveToState: (stateName, duration, onOverlayStart) => cameraControlsRef.current.moveToState?.(stateName, duration, onOverlayStart),
    // Add simple enterHouse function for doorbell interaction
    enterHouse: () => {
      console.log('Entering house...')
      // Mark that user has entered the house
      setHasEnteredHouse(true)
      // Move camera to lounge
      cameraControlsRef.current.moveToState?.('lounge')
      // Show new interactions - include office navigation
      setTimeout(() => {
        setVisibleInteractions(['gallery', 'recordPlayer', 'officeStairs'])
      }, 1000)
    },
    // Add simple showConversation function for doorbell
    startConversation: () => {
      // Move to doorbell view first, then show conversation overlay with timing
      cameraControlsRef.current.moveToState?.('doorbell', 2, () => setShowConversation(true))
    },
    closeConversation: () => {
      setShowConversation(false)
    },
    // Add gallery interaction function
    viewGallery: () => {
      console.log('Viewing gallery...')
      cameraControlsRef.current.moveToState?.('gallery', 2, () => setShowGallery(true))
    },
    // Add close gallery function
    closeGallery: () => {
      setShowGallery(false)
    },
    // Add music interaction function
    playMusic: () => {
      console.log('Playing music...')
      cameraControlsRef.current.moveToState?.('music', 2, () => setShowMusic(true))
    },
    // Add close music function
    closeMusic: () => {
      setShowMusic(false)
    },
    // Add office interaction functions
    viewAbout: () => {
      console.log('Viewing about...')
      cameraControlsRef.current.moveToState?.('about', 2, () => setShowAbout(true))
    },
    viewContact: () => {
      console.log('Viewing contact...')
      cameraControlsRef.current.moveToState?.('contact', 2, () => setShowContact(true))
    },
    // Add close contact function
    closeContact: () => {
      setShowContact(false)
    },
    // Add close about function
    closeAbout: () => {
      setShowAbout(false)
    },
    // Add close credits function
    closeCredits: () => {
      setShowCredits(false)
    },
    // Add navigation to office
    enterOffice: () => {
      console.log('Entering office...')
      cameraControlsRef.current.moveToState?.('office')
      // Show office interactions
      setTimeout(() => {
        setVisibleInteractions(['monitorScreen', 'notebook'])
      }, 1000)
    },
    // Add ESC navigation function
    goBack: () => {
      const currentState = cameraControlsRef.current.getCurrentState?.()
      const parentState = CAMERA_PARENTS[currentState]
      if (parentState) {
        console.log(`Going back from ${currentState} to ${parentState}`)
        cameraControlsRef.current.moveToState?.(parentState)
        
        // Update visible interactions based on parent state
        if (parentState === 'lounge') {
          setVisibleInteractions(['gallery', 'recordPlayer', 'officeStairs'])
        } else if (parentState === 'office') {
          setVisibleInteractions(['monitorScreen', 'notebook'])
        }
      }
    }
  }

  return (
    <CameraContext.Provider value={value}>
      {children}
    </CameraContext.Provider>
  )
}

// Camera Controller with GSAP animations
export function CameraController() {
  const { camera } = useThree()
  const { cameraControlsRef, goBack, setVisibleInteractions, hasEnteredHouse, setCurrentCameraState, sceneRefs } = useCamera()
  const { setUserLocation } = useMusicPlayer()
  const { activeOverlay } = useOverlayManager()
  const { contextSafe } = useGSAP()
  const isAnimatingRef = useRef(false)
  const currentTimelineRef = useRef(null)
  const [cameraState, setCameraState] = useState('outside')
  
  // Mouse drag controls
  const isDraggingRef = useRef(false)
  const lastMouseXRef = useRef(0)
  const baseRotationYRef = useRef(0) // Base rotation for current camera position
  const currentOffsetYRef = useRef(0) // Current offset from base rotation

  const posX = 0
  const posY = 1.8
  const posZ = 16
  const rotX = 0
  const rotY = 0


  // Initialize camera at outside position
  useEffect(() => {
    const outsideState = CAMERA_STATES.outside
    
    // Set rotation order to prevent gimbal lock and corkscrew rotation
    camera.rotation.order = 'YXZ'
    
    camera.position.set(...outsideState.position)
    camera.rotation.set(
      (outsideState.rotation[0] * Math.PI) / 180, // Convert degrees to radians
      (outsideState.rotation[1] * Math.PI) / 180,
      0 // Always keep Z rotation at 0
    )
    camera.fov = outsideState.fov
    camera.updateProjectionMatrix()
    setCameraState('outside')
    setCurrentCameraState('outside') // Update context state
    
    // Initialize base rotation for mouse controls
    baseRotationYRef.current = outsideState.rotation[1]
    currentOffsetYRef.current = 0
  }, [camera, setCurrentCameraState])

  // Mouse drag control handlers with easing
  const targetRotationRef = useRef(0) // Target rotation for easing
  const rotationTweenRef = useRef(null) // Current GSAP tween reference

  const handleMouseDown = (event) => {
    // Only enable drag on main camera positions
    if (!['outside', 'lounge', 'office'].includes(cameraState)) return
    
    isDraggingRef.current = true
    lastMouseXRef.current = event.clientX
    document.body.style.cursor = 'grabbing'
    
    // Kill any existing rotation tween to allow immediate response
    if (rotationTweenRef.current) {
      rotationTweenRef.current.kill()
    }
  }

  const handleMouseMove = (event) => {
    if (!isDraggingRef.current) return

    const deltaX = event.clientX - lastMouseXRef.current
    const rotationSpeed = 0.25 // Degrees per pixel (halved from 0.5)
    const maxRotation = 30 // Maximum degrees from center

    // Calculate new offset
    const newOffset = currentOffsetYRef.current + (deltaX * rotationSpeed)
    
    // Clamp to ±30 degrees
    const clampedOffset = Math.max(-maxRotation, Math.min(maxRotation, newOffset))
    currentOffsetYRef.current = clampedOffset

    // Calculate target rotation
    const totalRotationY = baseRotationYRef.current + clampedOffset
    targetRotationRef.current = (totalRotationY * Math.PI) / 180

    // Kill previous tween and create new one with longer easing
    if (rotationTweenRef.current) {
      rotationTweenRef.current.kill()
    }
    
    rotationTweenRef.current = gsap.to(camera.rotation, {
      y: targetRotationRef.current,
      duration: 0.3, // Double the duration (from 0.15 to 0.3)
      ease: "power2.out" // Smooth easing
    })

    lastMouseXRef.current = event.clientX
  }

  const handleMouseUp = () => {
    isDraggingRef.current = false
    document.body.style.cursor = 'default'
  }

  // Touch tracking refs
  const touchStartTimeRef = useRef(0)
  const touchStartPositionRef = useRef({ x: 0, y: 0 })
  const isActuallyDraggingRef = useRef(false)

  // Touch event handlers (equivalent to mouse events)
  const handleTouchStart = (event) => {
    // Only enable drag on main camera positions
    if (!['outside', 'lounge', 'office'].includes(cameraState)) return
    
    const target = event.target
    const canvas = document.querySelector('canvas')
    
    // Only proceed if touching the canvas directly (not an overlay or UI element)  
    if (target !== canvas) return
    
    // Use first touch point
    const touch = event.touches[0]
    if (!touch) return
    
    // Store touch start info but don't commit to dragging yet
    touchStartTimeRef.current = Date.now()
    touchStartPositionRef.current = { x: touch.clientX, y: touch.clientY }
    lastMouseXRef.current = touch.clientX
    isActuallyDraggingRef.current = false
    
    // Mark as potentially dragging but don't prevent default
    // This allows R3F to handle 3D object clicks normally
    isDraggingRef.current = true
  }

  const handleTouchMove = (event) => {
    if (!isDraggingRef.current) return
    
    // Use first touch point
    const touch = event.touches[0]
    if (!touch) return

    const currentTime = Date.now()
    const timeSinceStart = currentTime - touchStartTimeRef.current
    const totalMovement = Math.sqrt(
      Math.pow(touch.clientX - touchStartPositionRef.current.x, 2) +
      Math.pow(touch.clientY - touchStartPositionRef.current.y, 2)
    )

    // Only commit to camera drag if:
    // 1. Enough time has passed (200ms) OR enough movement (15px)
    // 2. This gives R3F time to handle object clicks
    if (timeSinceStart > 200 || totalMovement > 15) {
      if (!isActuallyDraggingRef.current) {
        // First time committing to drag - prevent default behaviors
        event.preventDefault()
        isActuallyDraggingRef.current = true
        
        // Kill any existing rotation tween
        if (rotationTweenRef.current) {
          rotationTweenRef.current.kill()
        }
      }
    }
    
    // Only perform camera rotation if we're actually dragging
    if (!isActuallyDraggingRef.current) return
    
    // Prevent scrolling/zooming now that we're committed to dragging
    event.preventDefault()
    
    const deltaX = touch.clientX - lastMouseXRef.current
    const rotationSpeed = 0.25 // Same speed as mouse
    const maxRotation = 30 // Same limit as mouse

    // Calculate new offset
    const newOffset = currentOffsetYRef.current + (deltaX * rotationSpeed)

    // Clamp to ±30 degrees
    const clampedOffset = Math.max(-maxRotation, Math.min(maxRotation, newOffset))
    currentOffsetYRef.current = clampedOffset

    // Calculate target rotation
    const totalRotationY = baseRotationYRef.current + clampedOffset
    targetRotationRef.current = (totalRotationY * Math.PI) / 180

    // Kill previous tween and create new one with smooth easing
    if (rotationTweenRef.current) {
      rotationTweenRef.current.kill()
    }
    
    rotationTweenRef.current = gsap.to(camera.rotation, {
      y: targetRotationRef.current,
      duration: 0.3,
      ease: "power2.out"
    })

    lastMouseXRef.current = touch.clientX
  }

  const handleTouchEnd = () => {
    isDraggingRef.current = false
    isActuallyDraggingRef.current = false
  }

  // Add global mouse and touch event listeners
  useEffect(() => {
    const canvas = document.querySelector('canvas')
    if (!canvas) return

    // Mouse events
    canvas.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    // Touch events for mobile
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false })
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd)

    return () => {
      // Mouse cleanup
      canvas.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      
      // Touch cleanup
      canvas.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [cameraState])

  // GSAP animation function with overlay timing
  const moveToState = contextSafe((stateName, duration = 2, onOverlayStart = null) => {
    const targetState = CAMERA_STATES[stateName]
    if (!targetState) return

    console.log(`Moving camera to ${stateName}`)
    
    // Dynamic duration based on transition type
    const currentState = cameraState
    const fastTransitions = [
      ['outside', 'doorbell'],
      ['doorbell', 'outside'],
      ['doorbell', 'lounge'],
      ['lounge', 'doorbell']
    ]
    
    // Check if this is a fast transition (1 second instead of 2)
    const isFastTransition = fastTransitions.some(([from, to]) => 
      (currentState === from && stateName === to) || 
      (currentState === to && stateName === from)
    )
    
    // Override duration if it's a fast transition and no custom duration was provided
    if (isFastTransition && duration === 2) {
      duration = 1.5
      console.log(`Fast transition detected: ${currentState} → ${stateName}, using 1s duration`)
    }
    
    // Kill any existing timeline to allow interruption
    if (currentTimelineRef.current) {
      currentTimelineRef.current.kill()
      console.log('Interrupted previous camera animation')
    }
    
    isAnimatingRef.current = true

    // Trigger door sound immediately when starting camera movement (not when completing)
    const isCurrentlyInside = ['lounge', 'office', 'gallery', 'music', 'about', 'contact'].includes(currentState)
    const isCurrentlyOutside = ['outside', 'doorbell'].includes(currentState)
    const isMovingInside = ['lounge', 'office', 'gallery', 'music', 'about', 'contact'].includes(stateName)
    const isMovingOutside = ['outside', 'doorbell'].includes(stateName)
    
    // Play door sound when transitioning between inside and outside (both directions)
    if ((isCurrentlyInside && isMovingOutside) || (isCurrentlyOutside && isMovingInside)) {
      console.log(`Triggering door sound - moving from ${isCurrentlyInside ? 'inside' : 'outside'} to ${isMovingInside ? 'inside' : 'outside'}`)
      // Trigger door sound through scene refs
      if (sceneRefs.current?.playDoorSound) {
        sceneRefs.current.playDoorSound()
      }
    }

    // Start light transition immediately when camera movement begins
    if (sceneRefs.current?.startLightTransition) {
      sceneRefs.current.startLightTransition(stateName)
    }

    // Set rotation order to prevent gimbal lock and unwanted Z rotation
    camera.rotation.order = 'YXZ'

    // Create timeline for smooth animation
    const timeline = gsap.timeline({
      onComplete: () => {
        setCameraState(stateName)
        setCurrentCameraState(stateName) // Update context state
        
        // Update base rotation and reset offset for new position
        baseRotationYRef.current = targetState.rotation[1]
        currentOffsetYRef.current = 0
        
        // Track user location for music autoplay
        const indoorStates = ['lounge', 'office', 'gallery', 'music', 'about', 'contact']
        const outdoorStates = ['outside', 'doorbell']
        
        if (indoorStates.includes(stateName)) {
          setUserLocation(true) // User is inside
        } else if (outdoorStates.includes(stateName)) {
          setUserLocation(false) // User is outside
        }
        
        isAnimatingRef.current = false
        currentTimelineRef.current = null
        console.log(`Camera reached ${stateName}`)
      }
    })

    // Schedule overlay animation to start 0.3 seconds before camera animation ends
    if (onOverlayStart) {
      const callbackDelay = Math.max(0, (duration - 0.3) * 1000) // Convert to milliseconds, ensure non-negative
      setTimeout(() => {
        onOverlayStart()
      }, callbackDelay)
    }
    
    // Store timeline reference for potential interruption
    currentTimelineRef.current = timeline

    // Animate position
    timeline.to(camera.position, {
      x: targetState.position[0],
      y: targetState.position[1],
      z: targetState.position[2],
      duration,
      ease: "power2.inOut"
    }, 0)

    // Animate rotation with explicit Z = 0
    timeline.to(camera.rotation, {
      x: (targetState.rotation[0] * Math.PI) / 180,
      y: (targetState.rotation[1] * Math.PI) / 180,
      z: 0, // Explicitly prevent Z rotation (roll)
      duration,
      ease: "power2.inOut",
      onUpdate: () => {
        // Ensure Z rotation stays at 0 during animation
        camera.rotation.z = 0
      }
    }, 0)

    // Animate FOV
    timeline.to(camera, {
      fov: targetState.fov,
      duration: duration * 0.8,
      ease: "power2.inOut",
      onUpdate: () => camera.updateProjectionMatrix()
    }, 0.2)
  })

  // Register functions in ref
  useEffect(() => {
    cameraControlsRef.current = {
      moveToState,
      getCurrentState: () => cameraState
    }
  }, [moveToState, cameraControlsRef, cameraState])

  // Watch for overlay changes and move camera accordingly
  useEffect(() => {
    if (activeOverlay && hasEnteredHouse) {
      // Map overlay names to camera states
      const overlayToCameraState = {
        'work': 'gallery',
        'about': 'about',
        'music': 'music',
        'contact': 'contact'
      }
      
      const targetState = overlayToCameraState[activeOverlay]
      if (targetState && cameraState !== targetState) {
        console.log(`Overlay ${activeOverlay} opened - moving camera to ${targetState}`)
        cameraControlsRef.current.moveToState?.(targetState)
        setVisibleInteractions([])
      }
    }
  }, [activeOverlay, hasEnteredHouse, cameraState, setVisibleInteractions])

  // ESC key listener for navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        // Don't handle ESC if any overlay is open - let overlays handle their own ESC logic
        if (activeOverlay) {
          return
        }
        
        event.preventDefault()
        // Special ESC behavior for different states
        if (cameraState === 'office') {
          console.log('ESC pressed in office - returning to lounge')
          cameraControlsRef.current.moveToState?.('lounge')
          setVisibleInteractions(['gallery', 'recordPlayer', 'officeStairs'])
        } else if (cameraState === 'doorbell') {
          console.log('ESC pressed at doorbell - returning to outside (position 1)')
          cameraControlsRef.current.moveToState?.('outside')
          setVisibleInteractions(['doorbell'])
        } else {
          goBack()
        }
      }
      
      // Arrow key navigation for room switching
      if (event.key === 'ArrowLeft' && hasEnteredHouse) {
        event.preventDefault()
        if (cameraState === 'lounge') {
          console.log('Left arrow pressed in lounge - moving to office')
          cameraControlsRef.current.moveToState?.('office')
          setVisibleInteractions(['monitorScreen', 'notebook'])
        }
      } else if (event.key === 'ArrowRight' && hasEnteredHouse) {
        event.preventDefault()
        if (cameraState === 'office') {
          console.log('Right arrow pressed in office - moving to lounge')
          cameraControlsRef.current.moveToState?.('lounge')
          setVisibleInteractions(['gallery', 'recordPlayer', 'officeStairs'])
        }
      }
      
      // Add number keys for quick navigation - restricted when outside house
      if (event.key === '1') {
        cameraControlsRef.current.moveToState?.('outside')
        setVisibleInteractions(['doorbell'])
      } else if (event.key === '2' && hasEnteredHouse) {
        cameraControlsRef.current.moveToState?.('lounge')
        setVisibleInteractions(['gallery', 'recordPlayer', 'officeStairs'])
      } else if (event.key === '3' && hasEnteredHouse) {
        cameraControlsRef.current.moveToState?.('office')
        setVisibleInteractions(['monitorScreen', 'notebook'])
      } else if (event.key === '4' && hasEnteredHouse) {
        cameraControlsRef.current.moveToState?.('gallery')
        setVisibleInteractions([])
      } else if (event.key === '5' && hasEnteredHouse) {
        cameraControlsRef.current.moveToState?.('music')
        setVisibleInteractions([])
      } else if (event.key === '6' && hasEnteredHouse) {
        cameraControlsRef.current.moveToState?.('about')
        setVisibleInteractions([])
      } else if (event.key === '7' && hasEnteredHouse) {
        cameraControlsRef.current.moveToState?.('contact')
        setVisibleInteractions([])
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [goBack, setVisibleInteractions, hasEnteredHouse, cameraState, activeOverlay])

  return null // No CameraControls component
}

// Hook to use camera system
export function useCamera() {
  const context = useContext(CameraContext)
  if (!context) {
    throw new Error('useCamera must be used within CameraProvider')
  }
  return context
}